import React, { useState, useCallback, useEffect, useMemo } from "react"
import BreadCrumb from "../breadcrumb";
import InputRadio from "../input-radio";
import Checkbox from "../checkbox";
import NoDataView from "../no-data-view";
import InputSearch from "../input-search";

// `onClick(item)` opens a navigable item (load its children there). Items
// flagged `selectable` get a radio (single) or checkbox (`multiple`) and toggle
// through `onSelect(item)`: a leaf toggles on a row click, a navigable item only
// from its control, since the row click opens it. Pass `onSelectAll(items, checked)`
// in multiple mode for a "Select all" row covering the selectable items listed.
// Inside a selected item everything below counts as selected, so it shows
// ticked and locked.
const FolderNavigation = ({ items, onClick, onSelect, className, isLoading, withSearch, multiple = false, onSelectAll, selected=[], emptyContent, children, breadCrumbs: breadCrumbsValue, onBreadCrumbChange, onBackButtonClick, disableBackButton, noDataText = "No items found" }) => {
  const [loading, setLoading] = useState(false);
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [searchText, setSearchText] = useState('');

  const updateBreadCrumb = useCallback((newBreadCrumb=[]) => {
    // This will fetch the items to display based on the breadcrumb
    let itemsToReturn = items;
    for(const breadCrumb of newBreadCrumb){
      itemsToReturn = itemsToReturn.find(item => item.id === breadCrumb.id)?.children || [];
    }
    setSearchText(""); // remove search text when breadcrumb changes
    setNavItems(itemsToReturn);
    setBreadCrumbs(newBreadCrumb);
    onBreadCrumbChange && onBreadCrumbChange(newBreadCrumb)
  },[items, onBreadCrumbChange])

  const handleClick = useCallback(async(item)=>{
    if(item.navigable === false){ // means if we not pass this flag that by default all things will be navigable
      if(item.selectable) onSelect?.(item)
      return
    }
    try {
      if(!item?.children && onClick){
        setLoading(true)
        await onClick(item)
      }
      updateBreadCrumb([...breadCrumbs, item])
    } finally {
      setLoading(false)
    }
  },[onClick, onSelect, breadCrumbs, updateBreadCrumb])

  const isWithinSelected = useMemo(() => breadCrumbs.some((breadCrumb) => selected.includes(breadCrumb.id)), [breadCrumbs, selected]);

  const filteredItems = useMemo(() => {
    // apply search filter
    return navItems.filter((item) => (item?.displayName || "").toLowerCase().includes(searchText.toLowerCase()));
  }, [navItems, searchText]);

  useEffect(() => {
    setNavItems(items)
    updateBreadCrumb(breadCrumbs)
  }, [items, breadCrumbs, updateBreadCrumb])

  useEffect(() => {
    if(breadCrumbsValue){
      updateBreadCrumb(breadCrumbsValue)
    }
  }, [breadCrumbsValue, updateBreadCrumb])
  
  return (
    <div className={`bimmatch-navigation ${className}`}>
      {!!breadCrumbs.length && <BreadCrumb breadCrumbs={breadCrumbs} onChange={updateBreadCrumb} onGoBack={onBackButtonClick} disableBackButton={disableBackButton} />}
      {children && children}
      {
        (isLoading || loading) ? (
          <div className={`page-loader`}></div>
        ) : (
          <>
            {!navItems.length ? (
              <div className="empty-folder-navigation-container">{emptyContent}</div>
            ) : (
              <>
                {withSearch && (
                  <div className="search-container">
                    <InputSearch searchText={searchText} onChangeSearchText={setSearchText} />
                  </div>
                )}
                {filteredItems.length ? (
                  <NavItems
                    items={filteredItems}
                    onItemClick={handleClick}
                    onItemSelect={onSelect}
                    multiple={multiple}
                    onSelectAll={multiple ? onSelectAll : undefined}
                    selected={selected}
                    locked={isWithinSelected}
                  />
                ) : (
                  <NoDataView 
                    noDataText={noDataText} 
                    size="medium" 
                  />
                )}
              </>
            )}
          </>
        )
      }
    </div>
  )

}

// A control click selects without opening the row; stopping it here also keeps
// a leaf from toggling twice (once from the control, once from the row).
const NavItems = ({ items=[], onItemClick, onItemSelect, multiple, onSelectAll, selected, locked }) => {
  const isItemSelected = useCallback((id) => locked || selected.includes(id), [locked, selected]);
  const selectableItems = useMemo(() => items.filter((item) => item.selectable), [items]);
  const allSelected = !!selectableItems.length && selectableItems.every((item) => isItemSelected(item.id));

  const selectFrom = (select) => (event) => {
    event.stopPropagation();
    if (!locked) select();
  };

  return (
    <div className={`nav-items-container ${locked ? 'locked' : ''}`}>
      {onSelectAll && !!selectableItems.length && (
        <div className="nav-item select-all" onClick={selectFrom(() => onSelectAll(selectableItems, !allSelected))}>
          <Checkbox value={allSelected} hideLabel />
          <p className="nav-item-label">Select all</p>
        </div>
      )}
      {items.map((item) => (
        <div
          className="nav-item"
          key={item.id}
          onClick={() => {
            // inside a selected folder only navigation stays live
            if (!locked || item.navigable) onItemClick?.(item);
          }}
        >
          {item.selectable && (
            <span className="nav-item-control" onClick={selectFrom(() => onItemSelect?.(item))}>
              {multiple ? (
                <Checkbox value={isItemSelected(item.id)} hideLabel />
              ) : (
                <InputRadio id={`nav-item-${item.id}`} checked={isItemSelected(item.id)} />
              )}
            </span>
          )}
          <p className="nav-item-label">{item.displayName}</p>
          {item.navigable && (
            <img
              src="/images/icon/arrow-right.svg"
              alt="right"
              className="right-arrow"
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default FolderNavigation